import {
  SxProps,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Theme,
} from '@mui/material';
import { formatInTimeZone } from 'date-fns-tz';

import { colors } from '@client/new/components/colors';
import { usDollarFormatter } from '@client/new/helpers/currency-formatter';
import { Typography } from '@design-system/components/typography/typography';

import { HeaderTableCell } from './components/HeaderTableCell';
import { PaymentStatus } from './components/PaymentStatus';
import { usePaymentMethod } from './usePaymentMethod';
import { EarningsBreakdown } from './useSubscriptionPayouts';

type RevenueTableRowProps = {
  name: string;
  revenue?: number;
  renderIfZero?: boolean;
  sx?: SxProps<Theme>;
};

const RevenueTableRow: React.FC<RevenueTableRowProps> = ({
  name,
  revenue,
  renderIfZero = false,
  sx,
}) => {
  if (revenue === undefined) {
    return null;
  }

  if (!renderIfZero && revenue === 0) {
    return null;
  }

  return (
    <TableRow sx={sx}>
      <TableCell align="left" className="p-4">
        <Typography>{name}</Typography>
      </TableCell>
      <TableCell align="right" className="p-4">
        <Typography>{usDollarFormatter.format(revenue)}</Typography>
      </TableCell>
    </TableRow>
  );
};

type AnnualFutureSectionProps = {
  futureAnnualRevenue?: number;
  futureRefundsAndDisputesRevenue?: number;
  totalFuturePayout?: number;
};

const AnnualFutureSection: React.FC<AnnualFutureSectionProps> = ({
  futureAnnualRevenue,
  futureRefundsAndDisputesRevenue,
  totalFuturePayout,
}) => {
  if (
    !futureAnnualRevenue &&
    !futureRefundsAndDisputesRevenue &&
    !totalFuturePayout
  ) {
    return null;
  }

  return (
    <Table
      sx={{
        marginTop: 5,
        minWidth: 650,
        boxShadow:
          '0px 1px 2px 0px rgba(29, 41, 57, 0.08), 0px 0px 1px 0px #EAECF0',
        borderCollapse: 'collapse',
        borderRadius: '10px',
      }}
    >
      <TableHead
        sx={{
          backgroundColor: colors.secondary200,
        }}
      >
        <TableRow>
          <HeaderTableCell
            title="Future"
            align="left"
            tooltip="Annual subscriptions are paid out in 12 equal installments over 12 months. This number represents total revenue that you will make in future months from these installments. Future deductions are included here."
          />
          <HeaderTableCell title="Net Revenue" align="right" />
        </TableRow>
      </TableHead>
      <TableBody>
        <RevenueTableRow name="Annual (Future)" revenue={futureAnnualRevenue} />
        <RevenueTableRow
          name="Refunds & Disputes"
          revenue={futureRefundsAndDisputesRevenue}
          renderIfZero
        />
        <RevenueTableRow
          name="Total Future Payouts"
          revenue={totalFuturePayout}
          sx={{ borderTop: '2px solid' }}
        />
      </TableBody>
    </Table>
  );
};

export const SubscriptionRevenue: React.FC<EarningsBreakdown> = (props) => {
  const paymentMethod = usePaymentMethod();

  if (!props.hasPrograms) {
    return null;
  }

  return (
    <div>
      <div className="flex gap-2 mb-5 items-center">
        <Typography variant="headline" size="small">
          Subscription Revenue
        </Typography>
        <PaymentStatus paidAt={props.paidAt} />
      </div>
      <div className="flex flex-col gap-[30px]"></div>
      <Table
        sx={{
          minWidth: 650,
          boxShadow:
            '0px 1px 2px 0px rgba(29, 41, 57, 0.08), 0px 0px 1px 0px #EAECF0',
          borderCollapse: 'collapse',
          borderRadius: '10px',
        }}
      >
        <TableHead
          sx={{
            backgroundColor: colors.secondary200,
          }}
        >
          <TableRow>
            <HeaderTableCell
              title={formatInTimeZone(props.date, 'UTC', 'MMMM yyyy')}
              align="left"
            />
            <HeaderTableCell title="Net Revenue" align="right" />
          </TableRow>
        </TableHead>
        <TableBody>
          <RevenueTableRow
            name="Limited Monthly"
            revenue={props.limitedMonthlyRevenue}
          />
          <RevenueTableRow
            name="Limited Annual"
            revenue={props.limitedAnnualRevenue}
          />
          <RevenueTableRow name="Monthly" revenue={props.monthlyRevenue} />
          <RevenueTableRow name="Quarterly" revenue={props.quarterlyRevenue} />
          <RevenueTableRow name="Annual" revenue={props.annualRevenue} />
          <RevenueTableRow
            name="Engagement"
            revenue={props.engagementRevenue}
          />
          <RevenueTableRow
            name="Refunds & Disputes"
            revenue={props.refundsAndDisputesRevenue}
            renderIfZero
          />
          <RevenueTableRow
            name="Total Subscription Revenue"
            revenue={props.totalSubscriptionRevenue}
            sx={{ borderTop: '2px solid' }}
            renderIfZero
          />
          <RevenueTableRow name="Subsidy" revenue={props.subsidy} />
          <RevenueTableRow
            name={`${paymentMethod.name} Transfer Fee`}
            revenue={props.payoutFee}
            renderIfZero
          />
          <RevenueTableRow
            name="Total Monthly Payout"
            revenue={props.totalPayout}
            sx={{ borderTop: '2px solid' }}
            renderIfZero
          />
          {props.totalPayout === 0 && (
            <TableRow>
              <TableCell align="left" className="p-4 text-red-400" colSpan={3}>
                <Typography>
                  Payouts are processed for earnings of{' '}
                  {usDollarFormatter.format(10)} or more.
                </Typography>
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
      <AnnualFutureSection
        futureAnnualRevenue={props.futureAnnualRevenue}
        futureRefundsAndDisputesRevenue={props.futureRefundsAndDisputesRevenue}
        totalFuturePayout={props.totalFuturePayout}
      />
    </div>
  );
};
